import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import translatePropTypes from '../../../translatePropTypes';
import withEditSession from '../../containers/EditSessionProvider/withEditSession';
import { FilterHidingContextProvider } from '../../controls/FilterBar';
import FormTitle from '../../utils/FormTitle';
import Spinner from '../../utils/Spinner';
import withTranslations from '../../utils/Translations/withTranslations';
import { CommonStaticTree } from '../BasicTrees';
import {
  getTreeLayoutConfig,
  treeLayoutProps,
  TreeLayoutTypeEnum,
} from '../BasicTrees/TreeLayoutConfig';
import StaticTreeDefaultFilter from './StaticTreeDefaultFilter';
import StaticTreeStaticBodyContent from './StaticTreeStaticBodyContent';
import Empty from '../../utils/Empty';

@withTranslations
@withEditSession
export default class StaticTree extends CommonStaticTree {
  static propTypes = {
    ...CommonStaticTree.propTypes,

    editNodeComponent: PropTypes.element,
    title: PropTypes.string,
    hasTitle: PropTypes.bool,
    paragraph: PropTypes.string,
    isLoading: PropTypes.bool,
    isSyntheticRootNodeAlwaysVisible: PropTypes.bool,
    onlyRootModelsAllowed: PropTypes.bool,
    hasFilterHiding: PropTypes.bool,
    dataCy: PropTypes.string,
    ...treeLayoutProps,
    ...translatePropTypes,
  };

  static defaultProps = {
    ...CommonStaticTree.defaultProps,

    editNodeComponent: null,
    title: null,
    hasTitle: false,
    onlyRootModelsAllowed: true,
    paragraph: null,
    isLoading: false,
    isSyntheticRootNodeAlwaysVisible: true,
    hasFilterHiding: false,
  };

  get title() {
    const { title, hasTitle, syntheticRootNodeName } = this.props;

    if (!hasTitle) {
      return null;
    }

    if (_.isString(title) || !title) {
      return <FormTitle title={title || syntheticRootNodeName} />;
    }

    return title;
  }

  get filter() {
    const {
      hasFilter,
      filter: {
        component,
        hasOmitDeleteStatus,
        hasSearchFieldOnly,
        hasDraftStatus,
        props,
      },
    } = this.props;

    if (!hasFilter) {
      return null;
    }

    const FilterBarComponent = component || StaticTreeDefaultFilter;

    const searchFieldWidthClassName = getTreeLayoutConfig(
      this.props.treeLayoutType || TreeLayoutTypeEnum.TREE_TABBED_PAGE,
    ).searchFieldWidthClassName;

    return (
      <FilterBarComponent
        hasDraftStatus={hasDraftStatus}
        hasOmitDeleteStatus={hasOmitDeleteStatus}
        hasSearchFieldOnly={hasSearchFieldOnly}
        searchFieldWidthClassName={searchFieldWidthClassName}
        value={this.props.filter.value || this.state.filter}
        onChange={this.handleFilterChange}
        {...props}
      />
    );
  }

  getTreeBody = plugins => {
    const {
      tree,
      filter: { value: filterValue },
      onSelectionChange,
      value,
      paragraph,
      editNodeComponent,
      isSyntheticRootNodeAlwaysVisible,
      treeLayoutType,
      onlyRootModelsAllowed,
    } = this.props;

    const { filter: stateFilterValue } = this.state;

    if (
      !onlyRootModelsAllowed &&
      this.models.length === this.rootModels.length
    ) {
      return <Empty />;
    }

    return (
      <StaticTreeStaticBodyContent
        adapter={this.state.adapter}
        dataCy={this.props.dataCy}
        editNodeComponent={editNodeComponent}
        filter={{
          value: filterValue || stateFilterValue,
          predicatesMeta: this.predicatesMeta(),
        }}
        getMainTreeChildrenCounts={this.getMainTreeChildrenCounts}
        inflateInitialExpansion={this.inflateInitialExpansion}
        isSyntheticRootNodeAlwaysVisible={isSyntheticRootNodeAlwaysVisible}
        models={this.models}
        nodeShouldBeInitiallyVisible={this.nodeShouldBeInitiallyVisible}
        paragraph={paragraph}
        plugins={plugins}
        rootModels={this.rootModels}
        selection={value}
        setMainTreeChildrenCounts={this.setMainTreeChildrenCounts}
        tree={tree}
        treeLayoutType={treeLayoutType}
        onSelectionChange={onSelectionChange}
      />
    );
  };

  get children() {
    const plugins = this.makePlugins();

    const { children, editNodeComponent, isLoading } = this.props;
    const { adapter } = this.state;

    const body = this.getTreeBody(plugins);

    if (children) {
      return children(body, this.filter, editNodeComponent, plugins, adapter, {
        title: this.title,
      });
    }

    return (
      <>
        {this.title}
        {this.filter}
        {isLoading ? <Spinner inline={false} /> : body}
      </>
    );
  }

  render() {
    const { hasFilterHiding } = this.props;

    if (hasFilterHiding) {
      return (
        <FilterHidingContextProvider>
          {this.children}
        </FilterHidingContextProvider>
      );
    }

    return this.children;
  }
}
